import { ButtonSpeedDial, NavigationBack, Typography } from "@/components";
import useCalendarItems from "@/hooks/useCalendarItems";
import { QUERY_KEYS } from "@/libraries/constant";
import { getMonths } from "@/libraries/date";
import { parseDate } from "@/libraries/dayjs-relative";
import { transformKeysToSnakeCase } from "@/libraries/transformers";
import {
	getCalendarDetailQueryOptions,
	getCalendarInfoQueryOptions,
} from "@/queries/options/calendar";
import {
	CalendarPageSearchSchema,
	type CreateOrUpdateCalendarFieldSchema,
} from "@/schemas";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AppBar from "@mui/material/AppBar";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import { onlineManager } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import pick from "lodash/pick";
import { Suspense, lazy, useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import CalendarEvents from "./-components/CalendarEvents";
import SwipeableEdgeDrawer from "./-components/DrawerDate";
import LoadingCalendar from "./-components/LoadingCalendar";

const TodoDetailDrawer = lazy(() => import("./-components/TodoDetailDrawer"));
const EventDetailDrawer = lazy(() => import("./-components/EventDetailDrawer"));

export const Route = createFileRoute("/dashboard/calendar/")({
	validateSearch: zodSearchValidator(CalendarPageSearchSchema),
	loaderDeps: ({ search }) => ({
		startDate: parseDate(search.date, "DD-MM-YYYY")
			.startOf("month")
			.format("DD-MM-YYYY"),
		endDate: parseDate(search.date, "DD-MM-YYYY")
			.endOf("month")
			.format("DD-MM-YYYY"),
	}),
	loader: ({ context, deps }) => {
		const infoParam = transformKeysToSnakeCase(deps);
		if (onlineManager.isOnline() && !context.isRestoring) {
			return [
				context.queryClient.ensureQueryData(
					getCalendarDetailQueryOptions(infoParam),
				),
				context.queryClient.ensureQueryData(
					getCalendarInfoQueryOptions(infoParam),
				),
			];
		}
		return [
			context.queryClient.getQueryData([
				QUERY_KEYS.GET_CALENDARS_DETAIL,
				infoParam,
			]),
			context.queryClient.getQueryData([
				QUERY_KEYS.GET_CALENDAR_INFO,
				infoParam,
			]),
		];
	},
	component: RouteComponent,
	pendingComponent: LoadingCalendar,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const startDate = Route.useLoaderDeps({
		select: (s) => s.startDate as string,
	});
	const endDate = Route.useLoaderDeps({
		select: (s) => s.endDate as string,
	});
	const searchDate = Route.useSearch({
		select: (s) => s.date,
	});
	const selectedDate = useMemo(
		() => parseDate(searchDate, "DD-MM-YYYY"),
		[searchDate],
	);
	const eventId = Route.useSearch({
		select: (s) => s.eid,
	});
	const reminderId = Route.useSearch({
		select: (s) => s.rid,
	});
	const days = useMemo(
		() => getMonths(parseDate(startDate, "DD-MM-YYYY")),
		[startDate],
	);

	const calendarItemProps = useMemo(
		() => ({
			days,
			startDate,
			endDate,
		}),
		[days, startDate, endDate],
	);
	const { events, reminders } = useCalendarItems(calendarItemProps);

	const selectedEvent = useMemo(
		() => events.find((event) => event.id === eventId),
		[events, eventId],
	);

	const selectedReminder = useMemo(
		() => reminders.find((reminder) => reminder.id === reminderId),
		[reminders, reminderId],
	);

	const { setValue } = useFormContext<CreateOrUpdateCalendarFieldSchema>();

	const onClickAdd = useCallback(() => {
		setValue("date", selectedDate);
		setValue("end", selectedDate.add(30, "minutes"));
		return navigate({
			to: "/dashboard/calendar/events/create",
			search: {
				date: searchDate,
			},
		});
	}, [searchDate, setValue, navigate, selectedDate]);

	return (
		<>
			<AppBar
				position="relative"
				color="transparent"
				className="[&_*_]:text-white !bg-gradient-case !relative !z-10"
			>
				<div className="container">
					<Toolbar classes={{ root: "!p-0" }}>
						<NavigationBack />
						<div className="w-full relative flex gap-1 justify-between py-3">
							<Typography fontWeight={700} fontSize={16}>
								Calendar
							</Typography>
						</div>
					</Toolbar>
				</div>
			</AppBar>
			<div className="relative flex-1 flex flex-col bg-gray-light overflow-hidden">
				<CalendarEvents selectedDate={selectedDate} {...calendarItemProps} />
				<SwipeableEdgeDrawer selectedDate={selectedDate} />
			</div>
			<Drawer
				anchor="bottom"
				open={!!selectedEvent || !!selectedReminder}
				classes={{
					root: "[&_.MuiPaper-root]:wrapper [&_.MuiPaper-root]:rounded-t-xl !z-[9999]",
				}}
				onClose={() =>
					navigate({
						replace: true,
						search: (oldSearch: CalendarPageSearchSchema) =>
							pick(oldSearch, ["date"]),
					})
				}
			>
				<div className="container py-4 flex flex-col justify-between items-start">
					<div className="w-full flex justify-end">
						<IconButton
							color="primary"
							onClick={() =>
								navigate({
									replace: true,
									search: (oldSearch: CalendarPageSearchSchema) =>
										pick(oldSearch, ["date"]),
								})
							}
						>
							<CloseRoundedIcon />
						</IconButton>
					</div>
					<Suspense>
						{selectedEvent ? <EventDetailDrawer event={selectedEvent} /> : null}
						{selectedReminder ? (
							<TodoDetailDrawer reminder={selectedReminder} />
						) : null}
					</Suspense>
				</div>
			</Drawer>
			<ButtonSpeedDial
				bottom={0}
				m={0}
				onClickAdd={onClickAdd}
				classes={{
					add: "!bg-gradient-camera2",
				}}
			/>
		</>
	);
}
